// @flow
import _ from 'lodash'
import type {
  AudienceDataForSegmentation,
  GroupedObjectParameters,
  GroupedStringParameters
} from '../types'
import type { State as Segmentation } from '../../redux/ducks/segmentation/reducers'

const importantParamsKeys = [
  'includedSegmentIds',
  'includedContactIds',
  'includedLookalikeSegmentIds',
  'includedLookalikeContactIds',
  'includedVkAppsGroups',
  'includedGroups',
  'vkActivityGroups',
  'keywordsGroups',
  'interestIds'
]
const allowedParamsForProviders = {
  vk: [
    'includedSegmentIds',
    'includedContactIds',
    'includedLookalikeSegmentIds',
    'includedLookalikeContactIds',
    'includedVkAppsGroups',
    'includedGroups',
    'vkActivityGroups',
    'interestIds'
  ],
  target_mail: [
    'includedSegmentIds',
    'includedContactIds',
    'includedLookalikeSegmentIds',
    'includedLookalikeContactIds',
    'includedVkAppsGroups',
    'includedGroups',
    'keywordsGroups',
    'interestIds'
  ],
  facebook: [
    'includedSegmentIds',
    'includedContactIds',
    'includedLookalikeSegmentIds',
    'includedLookalikeContactIds',
    'interestIds'
  ],
  google: ['includedSegmentIds', 'keywordsGroups', 'interestIds']
}

type emptyDiffType = {|
  includedSegmentIds: Array<number>,
  includedContactIds: Array<number>,
  includedLookalikeSegmentIds: Array<number>,
  includedLookalikeContactIds: Array<number>,
  includedVkAppsGroups: Array<GroupedStringParameters>,
  includedGroups: Array<GroupedObjectParameters>,
  vkActivityGroups: Array<GroupedObjectParameters>,
  keywordsGroups: Array<GroupedStringParameters>,
  interestIds: Array<number>
|}
const emptyDiff: emptyDiffType = {
  includedSegmentIds: [],
  includedContactIds: [],
  includedLookalikeSegmentIds: [],
  includedLookalikeContactIds: [],
  includedVkAppsGroups: [{ value: '', name: '' }],
  includedGroups: [{ value: [], name: '' }],
  vkActivityGroups: [{ value: [], name: '' }],
  keywordsGroups: [{ value: '', name: '' }],
  interestIds: []
}
const paramToSegmentationFlag = {
  includedSegmentIds: 'segmentByTargetingSources',
  includedContactIds: 'segmentByTargetingSources',
  includedLookalikeSegmentIds: 'segmentByTargetingSources',
  includedLookalikeContactIds: 'segmentByTargetingSources',
  includedVkAppsGroups: 'segmentByVkApps',
  includedGroups: 'segmentByVkGroups',
  vkActivityGroups: 'segmentByVkActivityGroup',
  keywordsGroups: 'segmentByKeywords',
  interestIds: 'segmentbyInterests'
}
const objectsWithCustomValues = [
  'includedGroups',
  'includedVkAppsGroups',
  'keywordsGroups',
  'vkActivityGroups'
]

// map over diffs
// separate each diff into array of diffs where each diff has only one important param
// there might be repeating diffs, so select only unique ones
export default function separateDiffs(
  diffs: Array<$Shape<AudienceDataForSegmentation>>,
  parametersToSegment: AudienceDataForSegmentation,
  segmentation: Segmentation
): Array<$Shape<AudienceDataForSegmentation>> {
  if (!segmentation.segmentByImportantParams) return diffs

  const filteredParams: $Shape<AudienceDataForSegmentation> = filterParams(parametersToSegment)
  if (_.isEmpty(filteredParams)) return diffs

  const result = _.uniqWith(
    _.flattenDeep(
      _.map(diffs, (diff) => {
        const separatedDiffs = separateDiff(
          diff,
          buildAllowedKeys(diff.providers),
          filteredParams,
          segmentation
        )
        return removeUnusedDiffs(separatedDiffs)
      })
    ),
    _.isEqual
  )
  return result
}

// build array of allowed params keys for providers
function buildAllowedKeys(providers: Array<string>): Array<string> {
  return _.uniq(
    _.flatten(
      _.map(providers, (provider) => {
        return allowedParamsForProviders[provider]
      })
    )
  )
}

// separate diff
// map over filtered important params
// if the key is not allowed for given providers, then return diff with empty important params values
// if the key is allowed, check if current key's value in diff is empty
// if it's not empty, then return diff with empty important params values apart from current key's value
// if it is empty, then check if we're segmenting by the current key
// if we're segmenting by the current key, then return diff with empty important params values
// because there already is a diff for the current key
// if we're not segmenting by the current key, then add current key's value from filtered important params
// here also might be repeating diffs, so select only unique ones
function separateDiff(
  diff: $Shape<AudienceDataForSegmentation>,
  allowedKeys: Array<string>,
  filteredParams: $Shape<AudienceDataForSegmentation>,
  segmentation: Segmentation
): Array<$Shape<AudienceDataForSegmentation>> {
  const filteredKeys = _.sortBy(_.keys(filteredParams))
  return _.uniqWith(
    _.map(filteredKeys, (key: string) => {
      const value = filteredParams[key]
      if (!_.includes(allowedKeys, key)) return { ...diff, ...emptyDiff }

      if (_.isEmpty(diff[key])) {
        const flagKey = paramToSegmentationFlag[key]
        return segmentation[flagKey]
          ? { ...diff, ...emptyDiff }
          : { ...diff, ...emptyDiff, [key]: value }
      } else {
        return { ...diff, ...emptyDiff, [key]: diff[key] }
      }
    }),
    _.isEqual
  )
}

// remove diffs with empty important params
// the problem is that we need the separated diff with empty important params
// if it's the only one for the diff we were separating
// so keep it if it's the only one
// and reject every separated diff with empty important params, if there are more than one separated diffs
function removeUnusedDiffs(
  separatedDiffs: Array<$Shape<AudienceDataForSegmentation>>
): Array<$Shape<AudienceDataForSegmentation>> {
  if (separatedDiffs.length === 1) return separatedDiffs

  return _.reject(separatedDiffs, (diff) => {
    const diffParams = _.pickBy(diff, (value, key: string) => {
      return _.includes(_.keys(emptyDiff), key)
    })

    return _.isEqual(emptyDiff, diffParams)
  })
}

// pick important params with non empty values
function filterParams(
  parametersToSegment: AudienceDataForSegmentation
): $Shape<AudienceDataForSegmentation> {
  return _.pickBy(parametersToSegment, (value: any, key: string) => {
    if (!_.includes(importantParamsKeys, key)) {
      return false
    } else if (_.includes(objectsWithCustomValues, key)) {
      return _.first(value) && !_.isEmpty(_.first(value).value)
    } else {
      return !_.isEmpty(value)
    }
  })
}
